import type { BaseUsecase } from '@usecases/base-usecase';
import type { Tag } from '@entities/tag';
import type { TagRepository } from '@infra/repositories/tag.repository';
import { ScvObjectManager } from '@main/sf-connector/scv-object-manager';
import { Logger } from '@main/utils/logger';

export interface FetchTagsEventPublisher {
  publish(): Promise<Tag[]>;
}

export interface FetchTagsUsecase extends BaseUsecase<void, Tag[]> {
  run(): Promise<Tag[]>;
}

/**
 * Fetches the account's available tags from the dialer bus, stores them in the
 * tag repository, and pushes them to the Salesforce widget so the call-ended
 * tagging screen can render them. Triggered fire-and-forget when wrap-up starts;
 * failures are logged and surfaced to the widget as an empty list.
 */
export class FetchTags implements FetchTagsUsecase {
  private logger = new Logger();

  constructor(
    private publisher: FetchTagsEventPublisher,
    private tagRepository: TagRepository,
  ) {}

  async run(): Promise<Tag[]> {
    try {
      const tags = await this.publisher.publish();
      this.tagRepository.setAvailableTags(tags);

      ScvObjectManager.notifyScv({
        type: 'SCV.TAGS_AVAILABLE',
        data: { tags },
      });

      this.logger.info('SCV: Sent available tags', { count: tags.length });
      return tags;
    } catch (error) {
      ScvObjectManager.notifyScv({
        type: 'SCV.TAGS_AVAILABLE',
        data: { tags: [] },
      });

      this.logger.error('SCV: Failed to fetch tags', { error });
      return [];
    }
  }
}
